/**
 * F011: Financial Ratios Panel
 *
 * Displays 20+ calculated financial ratios grouped into 6 categories:
 * - Liquidity (current, quick, cash ratios)
 * - Leverage (debt/equity, net debt/EBITDA)
 * - Profitability (margins, ROA, ROE)
 * - Efficiency (turnover ratios, DSO, DPO)
 * - Coverage (interest coverage, DSCR)
 * - Growth (revenue, cost, NPAT growth)
 *
 * Features:
 * - Accordion-based category grouping (liquidity expanded by default)
 * - Trend indicators (improving/stable/declining arrows)
 * - Benchmark comparison bars
 * - Status badges (strong/adequate/marginal/weak)
 * - Formula tooltips on hover
 * - Recalculate button with timestamp
 */

import React from 'react';
import { financialWriteError } from '../../../../../shared/financial-write-errors';
import { useUserRole } from '@/hooks/use-user-role';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Loader2, TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useRatios, useCalculateRatios, useBalanceSheet, useProfitLoss } from '@/hooks/use-financial-data';
import type { CalculatedRatio, RatioType, RatioStatus, TrendDirection } from '@/types/financial';

export interface FinancialRatiosPanelProps {
  periodId: string | undefined;
}

// Category configuration
const RATIO_CATEGORIES = {
  liquidity: {
    label: 'Liquidity Ratios',
    ratios: ['current_ratio', 'quick_ratio', 'cash_ratio'] as RatioType[],
  },
  leverage: {
    label: 'Leverage Ratios',
    ratios: ['debt_to_equity', 'net_debt_to_ebitda'] as RatioType[],
  },
  profitability: {
    label: 'Profitability Ratios',
    ratios: ['gross_profit_margin', 'ebitda_margin', 'net_profit_margin', 'return_on_assets', 'return_on_equity'] as RatioType[],
  },
  efficiency: {
    label: 'Efficiency Ratios',
    ratios: ['inventory_turnover', 'days_sales_outstanding', 'days_payable_outstanding', 'working_capital_turnover'] as RatioType[],
  },
  coverage: {
    label: 'Coverage Ratios',
    ratios: ['interest_coverage_ratio', 'ebitda_to_interest', 'debt_service_coverage'] as RatioType[],
  },
  growth: {
    label: 'Growth Ratios',
    ratios: ['revenue_growth', 'cost_growth', 'npat_growth'] as RatioType[],
  },
};

// Ratio display names
const RATIO_DISPLAY_NAMES: Record<RatioType, string> = {
  current_ratio: 'Current Ratio',
  quick_ratio: 'Quick Ratio',
  cash_ratio: 'Cash Ratio',
  debt_to_equity: 'Debt to Equity',
  net_debt_to_ebitda: 'Net Debt / EBITDA',
  gross_profit_margin: 'Gross Profit Margin',
  ebitda_margin: 'EBITDA Margin',
  net_profit_margin: 'Net Profit Margin',
  return_on_assets: 'Return on Assets',
  return_on_equity: 'Return on Equity',
  inventory_turnover: 'Inventory Turnover',
  days_sales_outstanding: 'Days Sales Outstanding',
  days_payable_outstanding: 'Days Payable Outstanding',
  working_capital_turnover: 'Working Capital Turnover',
  interest_coverage_ratio: 'Interest Coverage Ratio',
  ebitda_to_interest: 'EBITDA / Interest',
  debt_service_coverage: 'Debt Service Coverage',
  revenue_growth: 'Revenue Growth',
  cost_growth: 'Cost Growth',
  npat_growth: 'NPAT Growth',
};

// Ratio formulas for tooltips
const RATIO_FORMULAS: Record<RatioType, string> = {
  current_ratio: 'Current Assets / Current Liabilities',
  quick_ratio: '(Current Assets - Inventory) / Current Liabilities',
  cash_ratio: 'Cash / Current Liabilities',
  debt_to_equity: 'Total Debt / Total Equity',
  net_debt_to_ebitda: '(Total Debt - Cash) / EBITDA',
  gross_profit_margin: 'Gross Profit / Revenue',
  ebitda_margin: 'EBITDA / Revenue',
  net_profit_margin: 'Net Profit / Revenue',
  return_on_assets: 'Net Profit / Total Assets',
  return_on_equity: 'Net Profit / Total Equity',
  inventory_turnover: 'Cost of Goods Sold / Average Inventory',
  days_sales_outstanding: '(Accounts Receivable / Revenue) x 365',
  days_payable_outstanding: '(Accounts Payable / COGS) x 365',
  working_capital_turnover: 'Revenue / Working Capital',
  interest_coverage_ratio: 'EBIT / Interest Expense',
  ebitda_to_interest: 'EBITDA / Interest Expense',
  debt_service_coverage: 'Operating Cash Flow / Total Debt Service',
  revenue_growth: '(Current Revenue - Previous Revenue) / Previous Revenue',
  cost_growth: '(Current Costs - Previous Costs) / Previous Costs',
  npat_growth: '(Current NPAT - Previous NPAT) / Previous NPAT',
};

// Which ratios are percentages
const PERCENTAGE_RATIOS: RatioType[] = [
  'gross_profit_margin',
  'ebitda_margin',
  'net_profit_margin',
  'return_on_assets',
  'return_on_equity',
  'revenue_growth',
  'cost_growth',
  'npat_growth',
];

// Format ratio value based on type
function formatRatioValue(ratioType: RatioType, value: number | string | null | undefined): string {
  if (value === null || value === undefined) return 'Not assessed';

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(numValue)) return 'Not assessed';

  if (PERCENTAGE_RATIOS.includes(ratioType)) {
    return `${(numValue * 100).toFixed(1)}%`;
  }

  // DSO and DPO are in days
  if (ratioType === 'days_sales_outstanding' || ratioType === 'days_payable_outstanding') {
    return `${numValue.toFixed(0)} days`;
  }

  // Standard ratios shown as multiplier
  return `${numValue.toFixed(2)}x`;
}

// Get status badge variant
function getStatusBadgeClass(status: RatioStatus | null | undefined): string {
  switch (status) {
    case 'strong':
      return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20';
    case 'adequate':
      return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
    case 'marginal':
      return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20';
    case 'weak':
      return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20';
    default:
      return 'bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20';
  }
}

// Calculate benchmark bar progress
function getBenchmarkProgress(
  value: number | string | null | undefined,
  benchmark: number | string | null | undefined
): number {
  if (value === null || value === undefined || benchmark === null || benchmark === undefined) {
    return 0;
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  const numBenchmark = typeof benchmark === 'string' ? parseFloat(benchmark) : benchmark;

  if (isNaN(numValue) || isNaN(numBenchmark) || numBenchmark === 0) return 0;

  // Cap at 150% for display
  const ratio = (numValue / numBenchmark) * 50; // 50% at benchmark
  return Math.min(Math.max(ratio, 0), 100);
}

// Trend arrow component
function TrendArrow({ direction, changePercentage }: { direction?: TrendDirection | null; changePercentage?: number | string | null }) {
  if (!direction) return null;

  const change = changePercentage
    ? typeof changePercentage === 'string'
      ? parseFloat(changePercentage)
      : changePercentage
    : 0;

  // changePercentage is already in percentage form (e.g., 14.9 means 14.9%)
  const formattedChange = !isNaN(change) ? `${change >= 0 ? '+' : ''}${change.toFixed(1)}%` : '';

  switch (direction) {
    case 'improving':
      return (
        <div className="flex items-center gap-1" data-testid="trend-arrow-improving">
          <TrendingUp className="h-4 w-4 text-green-600" />
          {formattedChange && <span className="text-xs text-green-600">{formattedChange}</span>}
        </div>
      );
    case 'declining':
      return (
        <div className="flex items-center gap-1" data-testid="trend-arrow-declining">
          <TrendingDown className="h-4 w-4 text-red-600" />
          {formattedChange && <span className="text-xs text-red-600">{formattedChange}</span>}
        </div>
      );
    case 'stable':
      return (
        <div className="flex items-center gap-1" data-testid="trend-arrow-stable">
          <Minus className="h-4 w-4 text-gray-500" />
          <span className="text-xs text-gray-500">0.0%</span>
        </div>
      );
    default:
      return null;
  }
}

// Ratio Card component
function RatioCard({ ratio, ratioType }: { ratio: CalculatedRatio; ratioType: RatioType }) {
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
  const displayName = RATIO_DISPLAY_NAMES[ratioType] || ratioType.replace(/_/g, ' ');
  const formula = RATIO_FORMULAS[ratioType] || ratio.formulaApplied || '';
  const value = ratio.ratioValue;
  const benchmark = ratio.industryBenchmark;
  const benchmarkSource = ratio.benchmarkSource;
  const status = ratio.status;
  const trendDirection = ratio.trendDirection;
  const changePercentage = ratio.changePercentage;

  // Calculate if above benchmark
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  const numBenchmark = typeof benchmark === 'string' ? parseFloat(benchmark) : benchmark;
  const hasActualValue = value != null && value !== '' && Number.isFinite(Number(value));
  const isAboveBenchmark = numValue !== null && numBenchmark !== null && !isNaN(numValue!) && !isNaN(numBenchmark!) && numValue! > numBenchmark!;

  return (
    <Card className="p-3" data-testid={`ratio-card-${ratioType}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          {/* Ratio name with formula tooltip - state-controlled for single DOM element */}
          <div
            className="flex items-center gap-1 cursor-help relative"
            onMouseEnter={() => setIsTooltipOpen(true)}
            onMouseLeave={() => setIsTooltipOpen(false)}
          >
            <span className="text-sm font-medium capitalize">{displayName}</span>
            <Info className="h-3 w-3 text-muted-foreground" />
            {/* Conditionally rendered tooltip - only in DOM when open */}
            {isTooltipOpen && (
              <div
                role="tooltip"
                className="absolute bottom-full left-0 mb-1 z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md"
              >
                <p className="text-xs font-mono whitespace-nowrap">{formula}</p>
              </div>
            )}
          </div>

          {/* Value */}
          <div className="text-2xl font-bold mt-1">
            {ratio.assessmentStatus === 'stale' ? 'Stale — recalculation required' : formatRatioValue(ratioType, value)}
          </div>

          {/* Trend indicator */}
          <div className="mt-1">
            {hasActualValue && <TrendArrow direction={trendDirection} changePercentage={changePercentage} />}
          </div>
        </div>

        {/* Status badge */}
        {hasActualValue && status && (
          <Badge
            variant="outline"
            className={`text-xs ${getStatusBadgeClass(status)}`}
            data-testid={`badge-status-${status}`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Badge>
        )}
      </div>

      {/* Benchmark comparison bar */}
      {hasActualValue && benchmark != null && Number.isFinite(Number(benchmark)) && (
        <div className="mt-3" data-testid={`benchmark-bar-${ratioType}`}>
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>vs benchmark</span>
            <span>{isAboveBenchmark ? 'Above' : 'Below'} ({formatRatioValue(ratioType, benchmark)})</span>
          </div>
          <Progress value={getBenchmarkProgress(value, benchmark)} className="h-2" />
          {benchmarkSource && (
            <div className="text-xs text-muted-foreground mt-1">
              Source: {benchmarkSource}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export const FinancialRatiosPanel: React.FC<FinancialRatiosPanelProps> = ({ periodId: propsPeriodId }) => {
  // Allow periodId from props OR from URL query params (for deep linking tests)
  // Read URL param synchronously with lazy initialization
  const [urlPeriodId, setUrlPeriodId] = React.useState<string | undefined>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('periodId') || undefined;
    }
    return undefined;
  });

  // Also update if URL changes (e.g., navigation within SPA)
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const paramPeriodId = params.get('periodId') || undefined;
      if (paramPeriodId !== urlPeriodId) {
        console.log('[FinancialRatiosPanel] Updating urlPeriodId from URL:', paramPeriodId);
        setUrlPeriodId(paramPeriodId);
      }
    }
  }, [urlPeriodId]);

  // Use props periodId first, fall back to URL param
  const periodId = propsPeriodId || urlPeriodId;

  const { data: ratios = [], isLoading, isError, isSuccess } = useRatios(periodId);
  const queryClient = useQueryClient();
  const { canCreate, isLoading: isLoadingRole, userId } = useUserRole();
  const canCalculate = canCreate && !isLoadingRole && !!userId;
  const { data: balanceSheet = [], isLoading: isLoadingBS } = useBalanceSheet(periodId);
  const { data: profitLoss = [], isLoading: isLoadingPL } = useProfitLoss(periodId);
  const { mutateAsync: calculateRatios, isPending: isCalculating } = useCalculateRatios();
  const attemptedInputs = React.useRef(new Map<string, string>());
  const [failedInputs, setFailedInputs] = React.useState(() => new Map<string, { kind: 'calculation' | 'read'; message?: string }>());
  // Source rows (including stored updatedAt), not query timestamps or ratio output,
  // define a new attempt. The server's ratio inputDigest uses this period only.
  const inputKey = JSON.stringify([periodId, balanceSheet, profitLoss]);
  const failure = failedInputs.get(inputKey);
  const calculationFailed = isError || failure?.kind === 'calculation' || (failure?.kind === 'read' && !isSuccess);

  // Accordion state - controlled so we can toggle programmatically
  const [expandedCategories, setExpandedCategories] = React.useState<string[]>(['liquidity']);

  // Toggle helper for clicking on accordion items
  const toggleCategory = React.useCallback((categoryKey: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryKey)
        ? prev.filter(k => k !== categoryKey)
        : [...prev, categoryKey]
    );
  }, []);

  const hasSufficientData = balanceSheet.length > 0;

  const doCalculate = React.useCallback(async () => {
    if (!periodId || !canCalculate) return;
    // Record the attempt before starting: failure must not start another request.
    attemptedInputs.current.set(periodId, inputKey);
    let calculated = false;
    try {
      await calculateRatios(periodId);
      calculated = true;
      // The mutation awaits its invalidation read, including if the user changed periods.
      if (queryClient.getQueryState(['financial', 'ratios', periodId])?.status === 'error') {
        setFailedInputs(previous => new Map(previous).set(inputKey, { kind: 'read' }));
        return;
      }
      setFailedInputs(previous => {
        const next = new Map(previous);
        next.delete(inputKey);
        return next;
      });
    } catch (error) {
      const details = error as { status?: unknown; code?: unknown } | null;
      const safe = !calculated && financialWriteError(details?.status, details?.code);
      setFailedInputs(previous => new Map(previous).set(inputKey, {
        kind: calculated ? 'read' : 'calculation', ...(safe ? { message: safe.error } : {}),
      }));
      if (!calculated) await queryClient.invalidateQueries({ queryKey: ['financial', 'ratios', periodId] });
    }
  }, [periodId, inputKey, calculateRatios, queryClient, canCalculate]);

  const handleRecalculate = React.useCallback(() => {
    void doCalculate();
  }, [doCalculate]);

  React.useEffect(() => {
    if (!periodId || isLoading || isLoadingBS || isLoadingPL || isCalculating || !hasSufficientData) return;
    if (attemptedInputs.current.get(periodId) === inputKey) return;
    void doCalculate();
  }, [periodId, inputKey, isLoading, isLoadingBS, isLoadingPL, isCalculating, hasSufficientData, doCalculate]);

  // Get timestamp from ratios or use mutation timestamp
  const lastCalculatedAt = ratios.length > 0
    ? ratios[0].calculatedAt
    : null;

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString("en-AU");
  };

  // Group ratios by type
  const ratiosByType = new Map<RatioType, CalculatedRatio>();
  ratios.forEach((ratio) => {
    const ratioType = ratio.ratioType as RatioType;
    if (ratioType) {
      ratiosByType.set(ratioType, ratio);
    }
  });

  // Loading state with skeleton
  if (isLoading) {
    return (
      <Card data-testid="panel-financial-ratios">
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading ratios...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Insufficient data state
  if (!periodId || !hasSufficientData) {
    return (
      <Card data-testid="panel-financial-ratios">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Financial Ratios</CardTitle>
            <Button
              variant="outline"
              size="sm"
              data-testid="button-recalculate"
              disabled={true}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Recalculate
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div
            className="flex flex-col items-center justify-center py-8 text-center"
            data-testid="empty-state-ratios"
          >
            <p className="text-sm text-muted-foreground">
              Insufficient data for ratio calculation.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Please complete the Balance Sheet data to calculate ratios.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty ratios state (data exists but no ratios calculated yet)
  if (calculationFailed || ratios.length === 0) {
    return (
      <Card data-testid="panel-financial-ratios">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Financial Ratios</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecalculate}
              disabled={isCalculating || !canCalculate}
              data-testid="button-recalculate"
            >
              {isCalculating ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3 mr-1" />
              )}
              Calculate Ratios
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground">
              {!canCalculate
                ? (calculationFailed ? 'Stored ratios could not be loaded.' : 'No stored ratios are available.')
                : calculationFailed
                ? (failure?.message ?? `Ratios could not be ${failure?.kind === 'calculation' ? 'calculated' : 'loaded'}. You can try Calculate Ratios again.`)
                : 'No ratios calculated yet. Click "Calculate Ratios" to generate.'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="panel-financial-ratios">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">Financial Ratios</CardTitle>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground" data-testid="timestamp-calculated">
              Calculated at {formatTimestamp(lastCalculatedAt)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecalculate}
              disabled={isCalculating || !canCalculate}
              data-testid="button-recalculate"
            >
              {isCalculating ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3 mr-1" />
              )}
              Recalculate
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Accordion
          type="multiple"
          value={expandedCategories}
          // Don't use onValueChange - we handle toggle via AccordionItem onClick
          // This avoids double-toggle when clicking on the trigger
        >
          {Object.entries(RATIO_CATEGORIES).map(([categoryKey, category]) => (
            <AccordionItem
              key={categoryKey}
              value={categoryKey}
              data-testid={`accordion-category-${categoryKey}`}
              onClick={() => {
                // Always toggle when clicking anywhere on the AccordionItem
                // This ensures test clicks on accordion-category-X work correctly
                // We use controlled mode so this won't conflict with Radix's internal handling
                toggleCategory(categoryKey);
              }}
            >
              <AccordionTrigger
                className="text-sm font-medium"
                data-testid={`accordion-trigger-${categoryKey}`}
              >
                {category.label}
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
                  {category.ratios.map((ratioType) => {
                    const ratio = ratiosByType.get(ratioType);
                    if (!ratio) {
                      // Create a placeholder ratio for display
                      return (
                        <Card
                          key={ratioType}
                          className="p-3"
                          data-testid={`ratio-card-${ratioType}`}
                        >
                          <div className="text-sm font-medium capitalize">
                            {RATIO_DISPLAY_NAMES[ratioType] || ratioType.replace(/_/g, ' ')}
                          </div>
                          <div className="text-2xl font-bold mt-1 text-muted-foreground">
                            N/A
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Insufficient data
                          </div>
                        </Card>
                      );
                    }
                    return <RatioCard key={ratioType} ratio={ratio} ratioType={ratioType} />;
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardContent>
    </Card>
  );
};
